import type { SoldAddOn, SoldItem, SoldLines, SoldService } from '../types/agreements.types';
import type {
    ThirdPartyAddOn,
    ThirdPartyInclusion,
    ThirdPartyInvoicingContents,
    ThirdPartyService,
} from '../types/thirdPartyAgreement.types';

const major = (minor: number): number => minor / 100;

const inclusionItem = (serviceId: string, inclusion: ThirdPartyInclusion, index: number): SoldItem => ({
    id: `${serviceId}-inclusion-${index}`,
    name: inclusion.description,
    description: null,
    code: null,
    quantity: 1,
    unitNet: 0,
    unitGross: major(inclusion.costGrossMinor),
    lineGross: major(inclusion.costGrossMinor),
    status: 'available',
});

const wholeServicePlaceholder = (serviceId: string): SoldItem => ({
    id: `${serviceId}-whole`,
    name: 'Whole service',
    description: null,
    code: null,
    quantity: 1,
    unitNet: 0,
    unitGross: 0,
    placeholder: true,
    status: 'available',
});

export const thirdPartyServiceId = (service: ThirdPartyService): string => `tp-service-${service.serviceNumber}`;

export const thirdPartyAddOnId = (addOn: ThirdPartyAddOn, index: number): string =>
    `tp-add-on-${index}-${addOn.contentId ?? 'none'}`;

const soldService = (service: ThirdPartyService): SoldService => {
    const id = thirdPartyServiceId(service);
    return {
        id,
        sequence: service.serviceNumber,
        name: service.name,
        serviceType: null,
        dueDate: service.dueDate,
        dueMileage: service.dueMileage,
        pricedAt: 'service',
        net: 0,
        gross: major(service.amountGrossMinor),
        items: [...service.inclusions.map((inclusion, index) => inclusionItem(id, inclusion, index)), wholeServicePlaceholder(id)],
    };
};

const soldAddOn = (addOn: ThirdPartyAddOn, index: number): SoldAddOn => ({
    id: thirdPartyAddOnId(addOn, index),
    name: addOn.name,
    description: null,
    quantity: 1,
    unitNet: 0,
    unitGross: major(addOn.amountGrossMinor),
    linkedServiceId: null,
    dueDate: addOn.dueDate,
    status: 'available',
});

export function thirdPartySoldLines(contents: ThirdPartyInvoicingContents): SoldLines {
    return { services: contents.services.map(soldService), addOns: contents.addOns.map(soldAddOn) };
}
